/* global EpgEnabled */
import React, { Component } from 'react';
import ReactTimeout from 'react-timeout';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import _ from 'lodash';
import classNames from 'classnames';
import BreadcrumbsComponent, { breadcrumbsFoxLiveParser } from 'components/Breadcrumbs';
import Loader from 'components/Loader';
import {
  getErrorComponent,
  getOomLivePlayerEnabled,
  isStaticRender,
  setLocalChannelCookies,
} from 'helpers/ContentHelper';
import { noop } from 'helpers/GlobalHelper';
import { setHelmetTags } from 'helpers/HelmetHelper';
import { now } from 'helpers/DateTimeHelper';
import { getLocation, getPreviousPathname } from 'helpers/RouteHelper';
import {
  Events,
  getSegmentLivePageName,
  getTealiumSourceCollectionGlobal,
  handleAnalytics,
  PageNames,
  trackPageReferrerAnalytics,
} from '@foxcorp/delta-web-analytics';
import { logEvent } from 'helpers/LogReportHelpers';
import { checkSameDayOfYear, getLiveDateRange } from './LiveHelpers';
import { clearLive, fetchLiveAction, fetchLivePanel, fetchLivestream } from './LiveActions';
import Style from './Live.module.scss';
import FoxLive from './FoxLive';

class Live extends Component {
  constructor(props) {
    super(props);
    const { globalVideo, defaultProgram, defaultChannel } = this.props;
    let selectedVideo = null;
    let selectedVideoData = null;
    if (globalVideo) {
      selectedVideoData = globalVideo;
      selectedVideo = _.get(globalVideo, 'livePlayerScreenUrl');
    }

    this.state = {
      selectedChannel: null,
      selectedDate: now(),
      selectedVideo,
      selectedVideoData,
      panelTimeouts: {},
      firstChannelClick: false,
      liveVideoFetched: false,
      newRelicStart: false,
      selectedChannelCallSign: null,
      // trackedAnalyticsReferrer is used in trackPageReferrerAnalytics
      // eslint-disable-next-line react/no-unused-state
      trackedAnalyticsReferrer: false,
      defaultChannel,
      defaultProgram,
    };

    this.trackPageReferrerAnalytics = trackPageReferrerAnalytics.bind(this);
    this.ignoreUnmountReferrerAnalytics = false;
  }

  componentDidMount() {
    const { globalVideo, setLivePage, startedPlayback } = this.props;
    if (setLivePage) setLivePage(true);
    this.setPageView();
    const playbackPersistedFromPreviousScreen = !!globalVideo && startedPlayback;
    logEvent('onLiveScreenStart');
    logEvent('onLiveScreenView');
    this.fetchLocalData({ preventGlobalPanelSet: playbackPersistedFromPreviousScreen });
    this.setState({ defaultChannel: null, newRelicStart: true });
  }

  // Many props that are not in the default props are causing rerenders
  shouldComponentUpdate(nextProps, nextState) {
    const newProps = this.getChangingProps(nextProps);
    const oldProps = this.getChangingProps(this.props);
    return !_.isEqual(newProps, oldProps) || !_.isEqual(nextState, this.state);
  }

  componentDidUpdate(prevProps, prevState) {
    const {
      accessToken,
      clearLive,
      coordinates,
      panels,
      routeParams,
      savedVideo,
      video,
    } = this.props;
    const {
      accessToken: prevAccessToken,
      coordinates: prevCoordinates,
      panels: prevPanels,
      routeParams: prevRouteParams,
      savedVideo: prevSavedVideo,
      video: prevVideo,
    } = prevProps;
    const { newRelicStart } = this.state;

    if (newRelicStart) {
      logEvent('onLiveScreenFinished');
      this.setState({ newRelicStart: false });
    }
    const userLocationChanged = !_.isEqual(prevCoordinates, coordinates);
    const panelsChanged = !prevPanels && panels;
    const accessTokenChanged = accessToken !== prevAccessToken;
    const savedVideoChanged = savedVideo !== prevSavedVideo;
    const videoIdChanged = video !== prevVideo;
    const urlVideoId = _.get(routeParams, 'videoId');
    const prevUrlVideoId = _.get(prevRouteParams, 'videoId');
    const urlVideoIdChanged = urlVideoId !== prevUrlVideoId;
    if (userLocationChanged || accessTokenChanged || savedVideoChanged || videoIdChanged) {
      this.fetchLocalData();
    }
    if (panelsChanged) this.setPanelTimeouts();
    if (urlVideoIdChanged) {
      clearLive();
      this.fetchLocalData();
    }
  }

  componentWillUnmount() {
    const { clearLive, setLivePage } = this.props;
    setLocalChannelCookies({ resetCookies: true });
    setLivePage(false);
    clearLive();
  }

  getChangingProps = props => {
    const {
      accessToken,
      account,
      adblock,
      anonymousId,
      breakpoint,
      campaignOverrides,
      coordinates,
      defaultChannel,
      defaultProgram,
      deviceId,
      errors,
      favoritesManager,
      globalPlayerDisabled,
      globalVideo,
      isAuthenticated,
      isHeader,
      isPlayerLoaded,
      livePanel,
      location,
      mvpdData,
      overlay,
      panels,
      params,
      preauthorizedResources,
      primeTimePanelCount,
      primeTimePanels,
      profileId,
      provider,
      routeParams,
      savedVideo,
      seo,
      startedPlayback,
      streamExit,
      video,
    } = props;
    return {
      accessToken,
      account,
      adblock,
      anonymousId,
      breakpoint,
      campaignOverrides,
      coordinates,
      defaultChannel,
      defaultProgram,
      deviceId,
      errors,
      favoritesManager,
      globalPlayerDisabled,
      globalVideo,
      isAuthenticated,
      isHeader,
      isPlayerLoaded,
      livePanel,
      location,
      mvpdData,
      overlay,
      panels,
      params,
      preauthorizedResources,
      primeTimePanelCount,
      primeTimePanels,
      profileId,
      provider,
      routeParams,
      savedVideo,
      seo,
      startedPlayback,
      streamExit,
      video,
    };
  };

  setPageView() {
    const { pageViewEvent, getUseUtagGlobal } = this.props;
    const sourceCollection = getTealiumSourceCollectionGlobal();
    const collection = sourceCollection ? { ...sourceCollection } : null;
    const useUtagGlobal = getUseUtagGlobal();

    if (!collection) {
      pageViewEvent({ pageName: PageNames.LIVE, useUtagGlobal });
      const callSign = _.get(window, 'utag_data.call_sign', null);
      handleAnalytics({
        event: Events.TOP_LEVEL_SECTION_LANDING,
        payload: {
          pageName: callSign ? `live tv:landing:${callSign}` : `$live tv:landing`,
          callSign,
        },
      });
    } else {
      pageViewEvent({
        pageName: getSegmentLivePageName({
          location: getLocation(window.referrer),
          previousPath: getPreviousPathname(),
        }),
        useUtagGlobal,
      });
    }
  }

  setPanelTimeouts() {
    const { clearTimeout, panels, setTimeout } = this.props;
    if (!panels) return;
    const { panelTimeouts: prevPanelTimeouts, selectedDate } = this.state;
    const currentDate = now();
    const sameDay = checkSameDayOfYear(currentDate, selectedDate);
    let duration = null;
    let timeoutId = null;
    const newTimeouts = {};
    _.forEach(panels.member, panel => {
      duration = panel.validFor;
      timeoutId = `panel-${panel.id}`;
      if (prevPanelTimeouts[timeoutId]) clearTimeout(prevPanelTimeouts[timeoutId]);
      if (sameDay) {
        newTimeouts[timeoutId] = setTimeout(() => {
          this.fetchPanelData(panel);
        }, duration);
      }
    });
    this.setState({ panelTimeouts: newTimeouts });
  }

  fetchLocalData = (options = {}) => {
    const { panelUrl } = options;
    const {
      coordinates,
      accessToken,
      deviceId,
      fetchLiveAction,
      fetchLivePanel,
      routeParams,
      savedVideo,
      video,
    } = this.props;
    const { selectedDate } = this.state;
    if (isStaticRender()) return;
    const urlVideoId = _.get(routeParams, 'videoId');
    if (!EpgEnabled && urlVideoId) {
      const selectedVideo =
        _.get(savedVideo, 'livePlayerScreenUrl') || _.get(video, 'livePlayerScreenUrl', null);

      this.setState({
        selectedVideo,
        selectedVideoData: savedVideo || video,
      });
      return;
    }
    const dateRange = getLiveDateRange(selectedDate);
    if (panelUrl) {
      fetchLivePanel({ accessToken, panelUrl, dateRange, coordinates, deviceId });
    } else {
      fetchLiveAction({ accessToken, dateRange, coordinates, deviceId });
    }
  };

  fetchPanelData(panel) {
    const { clearTimeout, setTimeout } = this.props;
    const url = panel['@id'];
    this.fetchLocalData({ panelUrl: url });
    const { id, validFor } = panel;
    const timeoutId = `panel-${id}`;
    const { panelTimeouts: prevPanelTimeouts } = this.state;
    const newTimeout = {};
    if (prevPanelTimeouts[timeoutId]) clearTimeout(prevPanelTimeouts[timeoutId]);
    newTimeout[timeoutId] = setTimeout(() => {
      this.fetchPanelData(panel);
    }, validFor);
    const panelTimeouts = _.merge(prevPanelTimeouts, newTimeout);
    this.setState({ panelTimeouts });
  }

  render() {
    const { coordinates, errors, globalVideo, panels, savedVideo, seo, setLoading } = this.props;
    const { selectedChannel, selectedVideo } = this.state;
    if (errors && !_.isArray(errors)) return getErrorComponent(errors);
    const hasPanels = !!(panels && panels.member && panels.member.length > 0);
    const showLoader = coordinates && EpgEnabled;
    if (showLoader && !hasPanels && !globalVideo) {
      return <Loader setLoading={setLoading} center />;
    }
    const outOfMarketLivePlayerUrl = getOomLivePlayerEnabled();
    const epgContainerClasses = [Style.epgWrap];
    const breadcrumbClasses = [Style.breadcrumbs];
    if (outOfMarketLivePlayerUrl) {
      epgContainerClasses.push(Style.noEpg);
      breadcrumbClasses.push(Style.noEpg);
    }
    const tags = setHelmetTags(seo);
    const breadcrumbsData = breadcrumbsFoxLiveParser(savedVideo);
    const breadcrumbs = (
      <BreadcrumbsComponent data={breadcrumbsData} customStyle={classNames(breadcrumbClasses)} />
    );
    return (
      <div>
        {tags}
        <FoxLive
          {...this.props}
          fetchLiveAction={fetchLiveAction}
          selectedChannel={selectedChannel}
          selectedVideo={selectedVideo}
        />
        {breadcrumbs}
      </div>
    );
  }
}

Live.defaultProps = {
  accessToken: null,
  account: null,
  adblock: false,
  anonymousId: null,
  campaignOverrides: {},
  clearInterval: noop,
  clearTimeout: noop,
  coordinates: null,
  countdownVideo: {},
  defaultChannel: null,
  defaultProgram: null,
  deviceId: null,
  errors: null,
  fetchLivestream: noop,
  globalPlayerDisabled: true,
  globalVideo: null,
  isAuthenticated: false,
  isHeader: false,
  isPlayerLoaded: false,
  livePanel: null,
  location: {},
  mvpdData: null,
  overlay: null,
  panels: null,
  params: {},
  preauthorizedResources: [],
  primeTimePanelCount: null,
  primeTimePanels: null,
  profileId: null,
  provider: {},
  routeParams: {},
  savedVideo: null,
  seo: null,
  setCoordinates: noop,
  setGlobalShouldResetStillWatching: noop,
  setInterval: noop,
  setIsPlayerLoaded: noop,
  setLivePage: noop,
  setLoading: null,
  setMVPDLogoutDataToAppState: noop,
  setMVPDToAppState: noop,
  setPreauthorizedResources: null,
  setProvider: noop,
  setTimeout: noop,
  startedPlayback: false,
  streamExit: false,
  video: null,
};

Live.propTypes = {
  accessToken: PropTypes.string,
  account: PropTypes.shape({}),
  adblock: PropTypes.bool,
  anonymousId: PropTypes.string,
  campaignOverrides: PropTypes.shape({}),
  clearInterval: PropTypes.func,
  clearLive: PropTypes.func.isRequired,
  clearTimeout: PropTypes.func,
  coordinates: PropTypes.shape({}),
  countdownVideo: PropTypes.shape({}),
  defaultChannel: PropTypes.string,
  defaultProgram: PropTypes.string,
  deviceId: PropTypes.string,
  errors: PropTypes.number,
  fetchLiveAction: PropTypes.func.isRequired,
  fetchLivePanel: PropTypes.func.isRequired,
  fetchLivestream: PropTypes.func,
  getUseUtagGlobal: PropTypes.func.isRequired,
  globalPlayerDisabled: PropTypes.bool,
  globalVideo: PropTypes.shape({}),
  isAuthenticated: PropTypes.bool,
  isHeader: PropTypes.bool,
  isPlayerLoaded: PropTypes.bool,
  livePanel: PropTypes.shape({}),
  location: PropTypes.shape({}),
  mvpdData: PropTypes.shape({}),
  overlay: PropTypes.string,
  pageViewEvent: PropTypes.func.isRequired,
  panels: PropTypes.shape({
    itemsPerPage: PropTypes.number,
    member: PropTypes.arrayOf(PropTypes.shape({})),
    totalItems: PropTypes.number,
  }),
  params: PropTypes.shape({}),
  preauthorizedResources: PropTypes.arrayOf(PropTypes.string),
  primeTimePanelCount: PropTypes.number,
  primeTimePanels: PropTypes.shape({}),
  profileId: PropTypes.string,
  provider: PropTypes.shape({}),
  routeParams: PropTypes.shape({}),
  savedVideo: PropTypes.shape({}),
  seo: PropTypes.shape({}),
  setCoordinates: PropTypes.func,
  setGlobalShouldResetStillWatching: PropTypes.func,
  setInterval: PropTypes.func,
  setIsPlayerLoaded: PropTypes.func,
  setLivePage: PropTypes.func,
  setLoading: PropTypes.func,
  setMVPDLogoutDataToAppState: PropTypes.func,
  setMVPDToAppState: PropTypes.func,
  setPreauthorizedResources: PropTypes.func,
  setProvider: PropTypes.func,
  setTimeout: PropTypes.func,
  startedPlayback: PropTypes.bool,
  streamExit: PropTypes.bool,
  video: PropTypes.shape({}),
};

const mapStateToProps = ({
  live: {
    currentTime,
    defaultChannel,
    defaultChannelVideo,
    defaultProgram,
    errors,
    panel,
    panels,
    primeTimePanels,
    savedVideo,
    seo,
    video,
  },
  globalVideo: {
    countdownVideo,
    disableRender: globalPlayerDisabled,
    startedPlayback,
    streamExit,
    video: globalVideo,
  },
}) => {
  const primeTimePanelCount = primeTimePanels && primeTimePanels.length;

  return {
    countdownVideo,
    currentTime,
    defaultChannel,
    defaultChannelVideo,
    defaultProgram,
    errors,
    globalPlayerDisabled,
    globalVideo,
    livePanel: panel,
    panels,
    primeTimePanelCount,
    primeTimePanels,
    savedVideo,
    seo,
    startedPlayback,
    streamExit,
    video,
  };
};

const TimeoutLive = ReactTimeout(
  connect(mapStateToProps, {
    clearLive,
    fetchLiveAction,
    fetchLivePanel,
    fetchLivestream,
  })(Live),
);

export default TimeoutLive;
